import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { api } from '../../shared/api/client';
import type { FinanceDashboard as Dashboard, FinanceOverview } from '../../shared/api/types';
import { Card, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatMoney } from './money';
import { addDays, monthEnd, periodLabel, shiftMonth, weekStart } from './period';

type Show = 'todo' | 'ingresos' | 'egresos';
const money = (c: number) => formatMoney(c, 'MXN');
/** Monto corto para ejes: $12.5 mil, $1.2 M. */
const short = (c: number) => {
  const v = Math.abs(c) / 100;
  const sign = c < 0 ? '-' : '';
  if (v >= 1_000_000) return `${sign}$${(v / 1_000_000).toFixed(1).replace(/\.0$/, '')} M`;
  if (v >= 1_000) return `${sign}$${(v / 1_000).toFixed(1).replace(/\.0$/, '')} mil`;
  return `${sign}$${Math.round(v)}`;
};

function periods(today: string) {
  const week = weekStart(today);
  const q = Math.floor((Number(today.slice(5, 7)) - 1) / 3) * 3 + 1;
  const qStart = `${today.slice(0, 4)}-${String(q).padStart(2, '0')}-01`;
  const lastMonth = shiftMonth(today, -1);
  const year = Number(today.slice(0, 4));
  return [
    { key: 'semana', label: 'Esta semana', from: week, to: addDays(week, 6) },
    { key: 'mes', label: 'Este mes', from: `${today.slice(0, 7)}-01`, to: monthEnd(today) },
    { key: 'mes-pasado', label: 'Mes pasado', from: lastMonth, to: monthEnd(lastMonth) },
    { key: 'trimestre', label: 'Este trimestre', from: qStart, to: monthEnd(shiftMonth(qStart, 2)) },
    { key: 'anio', label: 'Este año', from: `${year}-01-01`, to: `${year}-12-31` },
    { key: 'anio-pasado', label: 'Año pasado', from: `${year - 1}-01-01`, to: `${year - 1}-12-31` },
  ];
}

function bucketLabel(start: string, g: Dashboard['granularity']): string {
  const [y, m, d] = start.split('-').map(Number);
  const date = new Date(Date.UTC(y!, m! - 1, d!));
  if (g === 'month') return date.toLocaleDateString('es-MX', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  return date.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Ancho real del contenedor, para que las gráficas se dibujen nítidas a cualquier tamaño. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(640);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth || 640);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.round(e!.contentRect.width) || 640));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function Delta({ now, before, goodWhenUp }: { now: number; before: number; goodWhenUp: boolean }) {
  if (before === 0) return <span className="muted small">Sin datos del periodo anterior</span>;
  const pct = Math.round(((now - before) / Math.abs(before)) * 100);
  const up = pct >= 0;
  const good = up === goodWhenUp;
  return (
    <span className={`small ${good ? 'fin-delta--good' : 'fin-delta--bad'}`}>
      {up ? '▲' : '▼'} {Math.abs(pct)}% <span className="muted">vs periodo anterior</span>
    </span>
  );
}

/** Tablero de Finanzas: filtros arriba, totales, ingresos y gastos en el tiempo, por categoría y saldo por cuenta. */
export function FinanceDashboard({ churchId, overview, version }: { churchId: string; overview: FinanceOverview; version: number }) {
  const all = periods(overview.today);
  const [period, setPeriod] = useState(all[1]!);
  const [custom, setCustom] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [show, setShow] = useState<Show>('todo');
  const [picked, setPicked] = useState<string[]>([]);

  const kinds = show === 'ingresos' ? ['income'] : show === 'egresos' ? ['expense'] : ['income', 'expense'];
  const categories = overview.categories.filter((c) => kinds.includes(c.kind)).sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name, 'es') : a.kind === 'income' ? -1 : 1));
  const selected = picked.filter((id) => categories.some((c) => c.id === id));
  // Al elegir solo ingresos o egresos sin marcar categorías, se piden todas las de ese tipo.
  const categoryParam = selected.length ? selected : show === 'todo' ? [] : categories.map((c) => c.id);
  const qs = new URLSearchParams({ from: period.from, to: period.to, ...(accountId ? { accountId } : {}), ...(categoryParam.length ? { categories: categoryParam.join(',') } : {}) });
  const data = useAsync((signal) => api.get<Dashboard>(`/finance/churches/${churchId}/dashboard?${qs.toString()}`, signal), [churchId, qs.toString(), version]);
  const last = useRef<Dashboard | null>(null);
  if (data.status === 'ready') last.current = data.data;
  const d = data.status === 'ready' ? data.data : last.current;

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const showIncome = show !== 'egresos';
  const showExpense = show !== 'ingresos';
  const accounts = overview.accounts.filter((a) => !a.archived);

  return (
    <div className="fin-dash">
      <Card>
        <div className="fin-filters">
          <div className="chips" role="group" aria-label="Periodo">
            {all.map((p) => (
              <button key={p.key} type="button" className={`chip${!custom && period.key === p.key ? ' chip--active' : ''}`} aria-pressed={!custom && period.key === p.key} onClick={() => (setCustom(false), setPeriod(p))}>
                {p.label}
              </button>
            ))}
            <button type="button" className={`chip${custom ? ' chip--active' : ''}`} aria-pressed={custom} onClick={() => setCustom(true)}>
              Fechas…
            </button>
          </div>
          {custom ? (
            <div className="form__row">
              <div className="field">
                <label htmlFor="dash-from">Desde</label>
                <input id="dash-from" type="date" value={period.from} max={period.to} onChange={(e) => e.target.value && setPeriod({ ...period, key: 'custom', from: e.target.value })} />
              </div>
              <div className="field">
                <label htmlFor="dash-to">Hasta</label>
                <input id="dash-to" type="date" value={period.to} min={period.from} onChange={(e) => e.target.value && setPeriod({ ...period, key: 'custom', to: e.target.value })} />
              </div>
            </div>
          ) : null}
          <div className="form__row">
            <div className="field">
              <label htmlFor="dash-account">Cuenta</label>
              <select id="dash-account" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                <option value="">Todas las cuentas</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <span className="field__label">Mostrar</span>
              <div className="chips" role="radiogroup" aria-label="Mostrar">
                {(['todo', 'ingresos', 'egresos'] as Show[]).map((k) => (
                  <button key={k} type="button" role="radio" aria-checked={show === k} className={`chip${show === k ? ' chip--active' : ''}`} onClick={() => setShow(k)}>
                    {k === 'todo' ? 'Todo' : k === 'ingresos' ? 'Ingresos' : 'Gastos'}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="chips" role="group" aria-label="Categorías">
            <button type="button" className={`chip${selected.length === 0 ? ' chip--active' : ''}`} aria-pressed={selected.length === 0} onClick={() => setPicked([])}>
              Todas las categorías
            </button>
            {categories.map((c) => (
              <button key={c.id} type="button" className={`chip${selected.includes(c.id) ? ' chip--active' : ''}`} aria-pressed={selected.includes(c.id)} onClick={() => toggle(c.id)}>
                <span className={`fin-dot fin-dot--${c.kind}`} aria-hidden="true" /> {c.name}
              </button>
            ))}
          </div>
          <p className="muted small">{periodLabel(period.from, period.to)}</p>
        </div>
      </Card>

      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {!d ? <Loading /> : null}
      {d ? (
        <>
          <div className="fin-kpis">
            {showIncome ? (
              <div className="fin-kpi">
                <span className="muted small">
                  <span className="fin-dot fin-dot--income" aria-hidden="true" /> Ingresos
                </span>
                <strong className="fin-kpi__value">{money(d.incomeCents)}</strong>
                <Delta now={d.incomeCents} before={d.previous.incomeCents} goodWhenUp />
              </div>
            ) : null}
            {showExpense ? (
              <div className="fin-kpi">
                <span className="muted small">
                  <span className="fin-dot fin-dot--expense" aria-hidden="true" /> Gastos
                </span>
                <strong className="fin-kpi__value">{money(d.expenseCents)}</strong>
                <Delta now={d.expenseCents} before={d.previous.expenseCents} goodWhenUp={false} />
              </div>
            ) : null}
            {show === 'todo' ? (
              <div className="fin-kpi">
                <span className="muted small">Balance</span>
                <strong className={`fin-kpi__value${d.incomeCents - d.expenseCents < 0 ? ' money--negative' : ''}`}>{money(d.incomeCents - d.expenseCents)}</strong>
                <span className="muted small">Ingresos menos gastos</span>
              </div>
            ) : null}
          </div>
          <Card title={`${show === 'ingresos' ? 'Ingresos' : show === 'egresos' ? 'Gastos' : 'Ingresos y gastos'} ${d.granularity === 'day' ? 'por día' : d.granularity === 'week' ? 'por semana' : 'por mes'}`}>
            <TimeBars d={d} showIncome={showIncome} showExpense={showExpense} />
          </Card>
          <div className="columns">
            {showIncome ? (
              <Card title="Ingresos por categoría">
                <CategoryBars rows={d.byCategory.filter((r) => r.category.kind === 'income')} kind="income" />
              </Card>
            ) : null}
            {showExpense ? (
              <Card title="Gastos por categoría">
                <CategoryBars rows={d.byCategory.filter((r) => r.category.kind === 'expense')} kind="expense" />
              </Card>
            ) : null}
          </div>
          <Card title="Saldo por cuenta (hoy)">
            <AccountBars accounts={accounts} />
          </Card>
        </>
      ) : null}
    </div>
  );
}

/** Barras agrupadas en el tiempo, con eje en un solo monto y aviso al pasar el dedo o el ratón. */
function TimeBars({ d, showIncome, showExpense }: { d: Dashboard; showIncome: boolean; showExpense: boolean }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);
  useEffect(() => setHover(null), [d]);
  const H = 260;
  const left = 64;
  const bottom = 28;
  const top = 12;
  const plotW = Math.max(100, width - left - 8);
  const plotH = H - top - bottom;
  const n = d.series.length;
  const max = Math.max(1, ...d.series.map((s) => Math.max(showIncome ? s.incomeCents : 0, showExpense ? s.expenseCents : 0)));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => t * max);
  const slot = plotW / n;
  const series = [...(showIncome ? (['incomeCents'] as const) : []), ...(showExpense ? (['expenseCents'] as const) : [])];
  const barW = Math.max(2, Math.min(28, (slot - 4) / series.length - 2));
  const y = (v: number) => top + plotH - (v / max) * plotH;
  const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / 64))));
  const empty = d.series.every((s) => s.incomeCents === 0 && s.expenseCents === 0);
  const h = hover !== null ? d.series[hover] : null;

  return (
    <div className="fin-chart" ref={ref}>
      <div className="fin-legend small" aria-hidden="true">
        {showIncome ? (
          <span>
            <span className="fin-dot fin-dot--income" /> Ingresos
          </span>
        ) : null}
        {showExpense ? (
          <span>
            <span className="fin-dot fin-dot--expense" /> Gastos
          </span>
        ) : null}
      </div>
      {empty ? <p className="muted">No hay movimientos en este periodo.</p> : null}
      <svg width={width} height={H} role="img" aria-label="Gráfica de ingresos y gastos en el tiempo" onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={left} x2={left + plotW} y1={y(t)} y2={y(t)} className="fin-grid" />
            <text x={left - 8} y={y(t) + 4} textAnchor="end" className="fin-axis">
              {short(t)}
            </text>
          </g>
        ))}
        {d.series.map((s, i) => {
          const x0 = left + i * slot + (slot - series.length * (barW + 2)) / 2;
          return (
            <g key={s.start}>
              {hover === i ? <rect x={left + i * slot} y={top} width={slot} height={plotH} className="fin-hover" /> : null}
              {series.map((k, j) => {
                const v = s[k];
                const bh = Math.max(0, (v / max) * plotH);
                return v > 0 ? <path key={k} d={roundedTop(x0 + j * (barW + 2), y(v), barW, bh)} className={k === 'incomeCents' ? 'fin-bar--income' : 'fin-bar--expense'} /> : null;
              })}
              {i % every === 0 ? (
                <text x={left + i * slot + slot / 2} y={H - 8} textAnchor="middle" className="fin-axis">
                  {bucketLabel(s.start, d.granularity)}
                </text>
              ) : null}
              <rect x={left + i * slot} y={top} width={slot} height={plotH} fill="transparent" onMouseEnter={() => setHover(i)} onClick={() => setHover(i)} />
            </g>
          );
        })}
      </svg>
      {h && hover !== null ? (
        <div className="fin-tooltip small" style={{ left: Math.min(width - 190, Math.max(0, left + hover * slot + slot / 2 - 90)) }} role="status">
          <strong>{d.granularity === 'week' ? `Semana del ${bucketLabel(h.start, 'day')}` : bucketLabel(h.start, d.granularity)}</strong>
          {showIncome ? (
            <span>
              <span className="fin-dot fin-dot--income" /> Ingresos {money(h.incomeCents)}
            </span>
          ) : null}
          {showExpense ? (
            <span>
              <span className="fin-dot fin-dot--expense" /> Gastos {money(h.expenseCents)}
            </span>
          ) : null}
        </div>
      ) : null}
      <button type="button" className="link-button small" onClick={() => setTable((t) => !t)}>
        {table ? 'Ocultar tabla' : 'Ver como tabla'}
      </button>
      {table ? (
        <table className="fin-table small">
          <thead>
            <tr>
              <th>{d.granularity === 'day' ? 'Día' : d.granularity === 'week' ? 'Semana del' : 'Mes'}</th>
              {showIncome ? <th>Ingresos</th> : null}
              {showExpense ? <th>Gastos</th> : null}
            </tr>
          </thead>
          <tbody>
            {d.series.map((s) => (
              <tr key={s.start}>
                <td>{bucketLabel(s.start, d.granularity)}</td>
                {showIncome ? <td>{money(s.incomeCents)}</td> : null}
                {showExpense ? <td>{money(s.expenseCents)}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/** Barra con las esquinas de arriba redondeadas (4px) y la base plana sobre el eje. */
function roundedTop(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, w / 2, h);
  return `M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + w - r} Q${x + w},${y} ${x + w},${y + r} V${y + h} Z`;
}

function CategoryBars({ rows, kind }: { rows: Dashboard['byCategory']; kind: 'income' | 'expense' }) {
  if (rows.length === 0) return <p className="muted">Sin movimientos en este periodo.</p>;
  const max = Math.max(1, ...rows.map((r) => r.totalCents));
  const total = rows.reduce((n, r) => n + r.totalCents, 0);
  return (
    <ul className="fin-hbars">
      {rows.map((r) => (
        <li key={r.category.id} title={`${r.category.name}: ${money(r.totalCents)}`}>
          <span className="fin-hbars__label">{r.category.name}</span>
          <span className="fin-hbars__track">
            <span className={`fin-hbars__bar fin-bar--${kind}`} style={{ width: `${Math.max(1, (r.totalCents / max) * 100)}%` }} />
          </span>
          <span className="fin-hbars__value">
            {money(r.totalCents)} <span className="muted">{total > 0 ? `${Math.round((r.totalCents / total) * 100)}%` : ''}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function AccountBars({ accounts }: { accounts: FinanceOverview['accounts'] }) {
  if (accounts.length === 0) return <p className="muted">Aún no hay cuentas.</p>;
  const max = Math.max(1, ...accounts.map((a) => Math.abs(a.balanceCents)));
  return (
    <ul className="fin-hbars">
      {accounts.map((a) => (
        <li key={a.id}>
          <span className="fin-hbars__label">{a.name}</span>
          <span className="fin-hbars__track">
            <span className={`fin-hbars__bar ${a.balanceCents < 0 ? 'fin-bar--expense' : 'fin-bar--neutral'}`} style={{ width: `${Math.max(1, (Math.abs(a.balanceCents) / max) * 100)}%` }} />
          </span>
          <span className={`fin-hbars__value${a.balanceCents < 0 ? ' money--negative' : ''}`}>{formatMoney(a.balanceCents, a.currency)}</span>
        </li>
      ))}
    </ul>
  );
}
